import React, { useMemo, useCallback } from 'react';
import { useRole } from '../../context/roleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEdit,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import DataTable from '../common/DataTable';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';


const TaxesTable = ({
  taxes = [],
  loading = false,
  onViewTax,
  onEditTax,
  onDeleteTax,
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess } = useRole();

  const hoverColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.4),
    []
  );

  /**
   * formatPercentage(value)
   *
   * Utility to format numeric tax rates with two decimal places and a
   * percent sign. Returns '0%' for null/undefined/NaN values.
   */
  const formatPercentage = useCallback((value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0%';
    }
    return `${Number(value).toFixed(2)}%`;
  }, []);

  /**
   * columns
   *
   * Column configuration passed to the generic `DataTable`. Each column
   * defines label, alignment, sorting accessors and a renderer for the
   * cell content. The actions column wires up view/edit/delete callbacks.
   */
  const columns = useMemo(
    () => [
      {
        id: 'gst',
        label: t('taxes.table.columns.gst'),
        sortable: true,
        align: 'center',
        sortAccessor: (tax) => Number(tax?.gst || 0),
        renderCell: (tax) => (
          <div className="flex items-center justify-center gap-2">
            <span className="font-medium text-green-700">
              {formatPercentage(tax.gst)}
            </span>
          </div>
        ),
        skeleton: <div className="h-5 w-16 rounded bg-gray-300 mx-auto" />,
      },
      {
        id: 'cgst',
        label: t('taxes.table.columns.cgst'),
        sortable: true,
        align: 'center',
        sortAccessor: (tax) => Number(tax?.cgst || 0),
        renderCell: (tax) => (
          <div className="flex items-center justify-center gap-2">
            <span className="font-medium text-blue-700">
              {formatPercentage(tax.cgst)}
            </span>
          </div>
        ),
        skeleton: <div className="h-5 w-16 rounded bg-gray-300 mx-auto" />,
      },
      {
        id: 'sgst',
        label: t('taxes.table.columns.sgst'),
        sortable: true,
        align: 'center',
        sortAccessor: (tax) => Number(tax?.sgst || 0),
        renderCell: (tax) => (
          <div className="flex items-center justify-center gap-2">
            <span className="font-medium text-purple-700">
              {formatPercentage(tax.sgst)}
            </span>
          </div>
        ),
        skeleton: <div className="h-5 w-16 rounded bg-gray-300 mx-auto" />,
      },
      {
        id: 'igst',
        label: t('taxes.table.columns.igst'),
        sortable: true,
        align: 'center',
        sortAccessor: (tax) => Number(tax?.igst || 0),
        renderCell: (tax) => (
          <div className="flex items-center justify-center gap-2">
            <span className="font-medium text-orange-700">
              {formatPercentage(tax.igst)}
            </span>
          </div>
        ),
        skeleton: <div className="h-5 w-16 rounded bg-gray-300 mx-auto" />,
      },
      {
        id: 'actions',
        label: t('taxes.table.columns.actions'),
        width: '120px',
        align: 'center',
        alignItems: 'center',
        sortable: false,
        renderCell: (tax) => (
          <div className="flex items-center justify-center gap-2">
            {onViewTax && (
              <button
                onClick={() => onViewTax(tax)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.1),
                  color: UI_COLORS.NAVIGATION.ACTIVE,
                }}
                title={t('taxes.table.aria.viewTax')}
                aria-label={t('taxes.table.aria.viewTax')}
              >
                <FontAwesomeIcon icon={faEye} className="text-sm" />
              </button>
            )}
            {onEditTax && (
              <button
                onClick={() => onEditTax(tax)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.WARNING,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1),
                }}
                title={t('taxes.table.aria.editTax')}
                aria-label={t('taxes.table.aria.editTax')}
              >
                <FontAwesomeIcon icon={faEdit} className="text-sm" />
              </button>
            )}
            {onDeleteTax && checkDeleteAccess() && (
              <button
                onClick={() => onDeleteTax(tax)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.ERROR,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                }}
                title={t('taxes.table.aria.deleteTax')}
                aria-label={t('taxes.table.aria.deleteTax')}
              >
                <FontAwesomeIcon icon={faTrash} className="text-sm" />
              </button>
            )}
          </div>
        ),
        skeleton: (
          <div className="flex items-center justify-center gap-2">
            <div className="h-4 w-4 rounded bg-gray-300" />
            <div className="h-4 w-4 rounded bg-gray-300" />
            <div className="h-4 w-4 rounded bg-gray-300" />
          </div>
        ),
      },
    ],
    [formatPercentage, onViewTax, onEditTax, onDeleteTax, checkDeleteAccess, t]
  );

  /**
   * getRowStyle(tax)
   *
   * Returns per-row style overrides: transparent by default with a hover
   * background to match the table's visual language.
   */
  const getRowStyle = useCallback(
    (tax) => {
      return {
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: hoverColor,
        },
      };
    },
    [hoverColor]
  );

  const emptyStateConfig = useMemo(
    () => ({
      title: t('taxes.table.emptyState.title'),
      message: t('taxes.table.emptyState.message'),
    }),
    [t]
  );

  return (
    <DataTable
      data={taxes}
      columns={columns}
      loading={loading}
      emptyState={emptyStateConfig}
      getRowStyle={getRowStyle}
      enablePagination={false}
      className="taxes-table"
    />
  );
};

export default TaxesTable;